import React, { useState, useEffect } from "react"

import { Toggle } from "../../../../components/shared/Inputs"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"

// ColorInput component
const ColorInput = ({ label, value, onChange, className = "" }) => (
  <div className={`space-y-1 ${className}`}>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <div className="flex gap-2">
      <input
        type="color"
        value={value || "#ffffff"}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-12 cursor-pointer rounded border border-gray-light"
      />
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#ffffff"
        className="flex-1 rounded border border-gray-light px-2 py-1 text-sm"
      />
    </div>
  </div>
)

const EventsEditor = ({ section, practice, onSave, onCancel }) => {
  const { handleUpdateSectionPreview, clearPreview } = useWebsiteBuilderV2()
  const [formData, setFormData] = useState({
    headline: section?.headline || "Upcoming Events",
    settings: section?.settings || {
      layout: "list", // list, 2_highlights, 3_highlights
      showBackgroundColor: false,
      backgroundColor: "#f8f9fa"
    }
  })

  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (section) {
      setFormData({
        headline: section.headline || "Upcoming Events",
        settings: {
          layout: "list",
          showBackgroundColor: false,
          backgroundColor: "#f8f9fa",
          ...section.settings
        }
      })
    }
  }, [section, practice])

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }

    // Update preview immediately for content changes
    if (section?.id && ["headline", "subheadline", "body"].includes(field)) {
      handleUpdateSectionPreview(section.id, {
        ...section,
        ...formData,
        [field]: value
      })
    }
  }

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, [key]: value }
    }))

    // Update preview immediately for layout changes and other important settings
    if (section?.id && ["layout", "showBackgroundColor", "backgroundColor"].includes(key)) {
      const updatedSettings = { ...formData.settings, [key]: value }
      handleUpdateSectionPreview(section.id, {
        ...section,
        ...formData,
        settings: updatedSettings
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.headline.trim()) {
      newErrors.headline = "Headline is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (validateForm()) {
      onSave({
        layout: "events",
        ...formData
      })
    }
  }

  // Layout options with preview images - matching Figma design
  const layoutOptions = [
    {
      id: "list",
      name: "List",
      description: "Vertical list of events",
      image: require("../../../../../assets/images/builder/v2/layout-images/list.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/list-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/list-selected.png")
    },
    {
      id: "2_highlights",
      name: "2 Highlights",
      description: "Two highlighted events in a grid",
      image: require("../../../../../assets/images/builder/v2/layout-images/2-highlights.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/2-highlights-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/2-highlights-selected.png")
    },
    {
      id: "3_highlights",
      name: "3 Highlights",
      description: "Three highlighted events in a grid",
      image: require("../../../../../assets/images/builder/v2/layout-images/3-highlights.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/3-highlights-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/3-highlights-selected.png")
    }
  ]

  const LayoutOption = ({ option, isSelected, onSelect }) => {
    const [isHovered, setIsHovered] = useState(false)

    const getImageSource = () => {
      if (isSelected) {
        return option.imageSelected
      } else if (isHovered) {
        return option.imageHover
      } else {
        return option.image
      }
    }

    return (
      <div
        className="cursor-pointer transition-all"
        onClick={() => onSelect(option.id)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title={option.name}>
        <img
          src={getImageSource()}
          alt={`${option.name} layout`}
          className="h-16 w-24 rounded-lg object-cover transition-all duration-200"
          onError={(e) => {
            console.warn(`Failed to load image: ${e.target.src}`) // eslint-disable-line no-console
            e.target.style.display = "none"
            e.target.nextSibling.style.display = "block"
          }}
        />
        {/* Fallback placeholder */}
        <div className="hidden h-16 w-24 items-center justify-center rounded-lg bg-gray-200">
          <div className="text-xs text-gray-500">{option.name}</div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Form Content */}
      <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pt-6">
        {/* Layout Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Layout</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Select a layout design for this section:</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 content-start items-start justify-start gap-3 text-clip p-0 [flex-flow:wrap]">
            {layoutOptions.map((option) => (
              <LayoutOption
                key={option.id}
                option={option}
                isSelected={formData.settings.layout === option.id}
                onSelect={(layoutId) => handleSettingsChange("layout", layoutId)}
              />
            ))}
          </div>
        </div>

        {/* Title Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Title</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">
                Create a powerful headline to capture attention and communicate your core message.
              </p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <input
                type="text"
                value={formData.headline}
                onChange={(e) => handleChange("headline", e.target.value)}
                placeholder="Upcoming Events"
                className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                required
              />
            </div>
            {errors.headline && (
              <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-red-500">
                <p className="block leading-[19.5px]">{errors.headline}</p>
              </div>
            )}
          </div>
        </div>

        {/* Section Background Color Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Section background color</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Include a background color in this section.</p>
            </div>
          </div>
          <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
            <Toggle
              checked={formData.settings.showBackgroundColor || false}
              onChange={(checked) => handleSettingsChange("showBackgroundColor", checked)}
            />
            <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[0] text-[#0b3954]">
              <p className="block whitespace-pre leading-[24px]">Background color</p>
            </div>
          </div>

          {formData.settings.showBackgroundColor && (
            <ColorInput
              label="Background Color"
              value={formData.settings.backgroundColor}
              onChange={(value) => handleSettingsChange("backgroundColor", value)}
              className="mt-4"
            />
          )}
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="box-border flex w-full flex-row content-stretch items-center justify-between border-t border-[#e4eff6] bg-white p-[32px]">
        <div className="relative box-border flex min-h-px min-w-px shrink-0 grow basis-0 flex-row content-stretch items-center justify-end gap-4 p-0">
          <button
            onClick={() => {
              clearPreview() // Clear any preview changes
              onCancel()
            }}
            className="relative box-border flex shrink-0 cursor-pointer flex-row content-stretch items-center justify-center gap-2 overflow-visible rounded border border-[#c8d6df] bg-white px-4 py-0 transition-colors hover:bg-gray-50">
            <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
              <p className="block whitespace-pre leading-[32px]">Close</p>
            </div>
          </button>
          <button
            onClick={handleSubmit}
            className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-center gap-2 rounded bg-[#0d9da4] px-4 py-0 transition-colors hover:bg-[#0b8a90]">
            <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-white">
              <p className="block whitespace-pre leading-[32px]">Save</p>
            </div>
          </button>
        </div>
      </div>
    </>
  )
}

export default EventsEditor
